import { Link } from "react-router-dom";
import {
  ArrowRight, Brain, Lightbulb, BarChart3, MessageSquare, Bot,
  Eye, Cpu, Link2, ShieldCheck, Search, PenTool, Wrench,
  Activity, Repeat, CheckCircle, Globe, Users, Lock, Sparkles
} from "lucide-react";

const aiServices = [
  {
    icon: Lightbulb,
    title: "AI Strategy & Consulting",
    items: [
      "AI readiness assessments and opportunity identification",
      "Business aligned AI roadmaps and use case prioritization",
      "Responsible AI, governance, and compliance alignment",
    ],
  },
  {
    icon: BarChart3,
    title: "Machine Learning & Predictive Analytics",
    items: [
      "Predictive models for forecasting demand, risk, and performance",
      "Classification and recommendation systems for operational insights",
      "Data driven intelligence to support strategic and operational decisions",
    ],
  },
  {
    icon: MessageSquare,
    title: "Natural Language Processing (NLP)",
    items: [
      "Text analytics, sentiment analysis, and document intelligence",
      "Intelligent search, summarization, and knowledge extraction",
      "AI driven conversational interfaces and language understanding",
    ],
  },
  {
    icon: Bot,
    title: "Intelligent Chatbots & Virtual Assistants",
    items: [
      "AI powered chatbots for customer, employee, and service workflows",
      "24x7 conversational support across web, mobile, and enterprise platforms",
      "Integration with ERP, CRM, ITSM, and enterprise applications",
    ],
  },
  {
    icon: Eye,
    title: "Computer Vision & Intelligent Automation",
    items: [
      "Image and video analytics for inspection, monitoring, and recognition",
      "Intelligent document processing (OCR, classification, data extraction)",
      "Automation of visual and unstructured data workflows",
    ],
  },
  {
    icon: Cpu,
    title: "Decision Intelligence & AI Driven Insights",
    items: [
      "AI enabled decision support systems",
      "Rule based and probabilistic AI models",
      "Real time analytics dashboards for leadership and operations",
    ],
  },
  {
    icon: Link2,
    title: "AI Integration with Enterprise Applications",
    items: [
      "AI embedded into ERP, CRM, SCM, and core business systems",
      "Secure API based integration with enterprise data platforms",
      "Clean core, upgrade safe AI enablement",
    ],
  },
];

const governance = [
  { icon: Lock, text: "Secure AI architectures aligned with enterprise security standards" },
  { icon: ShieldCheck, text: "Data privacy and compliance support (GDPR, DPDPA, ISO 42001)" },
  { icon: Search, text: "Explainable AI, model transparency, and audit readiness" },
  { icon: CheckCircle, text: "Ethical AI and risk controls embedded by design" },
];

const deliveryPhases = [
  { step: "01", icon: Search, title: "Discover", desc: "Identify high impact business use cases" },
  { step: "02", icon: PenTool, title: "Design", desc: "Build scalable, secure AI architectures" },
  { step: "03", icon: Wrench, title: "Develop", desc: "Train and validate AI/ML models" },
  { step: "04", icon: Link2, title: "Integrate", desc: "Embed AI into enterprise workflows" },
  { step: "05", icon: Activity, title: "Operate", desc: "Monitor performance, security, and compliance" },
  { step: "06", icon: Repeat, title: "Optimize", desc: "Continuous learning and model improvement" },
];

const businessBenefits = [
  "Faster, data driven decision making",
  "Reduced operational costs through intelligent automation",
  "Improved customer and employee experiences",
  "Scalable AI aligned with enterprise governance",
  "Secure, compliant, and future ready AI adoption",
];

const whyTeqxl = [
  { icon: Globe, title: "Enterprise First", desc: "Enterprise first AI approach, not experimental deployments" },
  { icon: Link2, title: "Deep Integration", desc: "Deep integration with Enterprise Applications and ERP ecosystems" },
  { icon: ShieldCheck, title: "Security & Compliance", desc: "Strong focus on security, compliance, and governance" },
  { icon: Sparkles, title: "Outcome Driven", desc: "Business outcome driven AI solutions" },
  { icon: Users, title: "End to End", desc: "End to end delivery from strategy to operations" },
];

export default function AIServicePage() {
  return (
    <div data-testid="ai-service-page">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
                Artificial Intelligence (AI) Services
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
                Enterprise Grade AI for Intelligent Decision Making
              </h1>
              <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-4">
                TEQXL delivers enterprise ready Artificial Intelligence solutions designed to
                enhance operational efficiency, decision making, and digital innovation. Our AI
                services combine data intelligence, machine learning, automation, and enterprise
                integration to help organizations transform processes, unlock insights, and scale
                responsibly.
              </p>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-8">
                We focus on business driven AI — secure, compliant, and seamlessly integrated
                with enterprise applications and IT ecosystems.
              </p>
              <Link
                to="/contact"
                data-testid="ai-hero-cta"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001F70] transition-colors"
              >
                Explore AI Solutions <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="text-center">
              <img
                src="/ai-services.png"
                alt="Enterprise AI Across Industries"
                className="w-full h-auto lg:h-[400px] lg:object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* AI & Intelligent Automation Services */}
      <section data-testid="ai-services-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Our AI & Intelligent Automation Services
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Comprehensive enterprise AI capabilities
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-gray-200 bg-white">
            {aiServices.map((service, i) => (
              <div
                key={service.title}
                data-testid={`ai-service-item-${i}`}
                className={`p-8 lg:p-10 border-b border-gray-200 ${
                  i === aiServices.length - 1 ? "border-b-0" : ""
                } ${(i + 1) % 3 !== 0 ? "lg:border-r border-gray-200" : ""} ${
                  (i + 1) % 2 !== 0 ? "sm:border-r lg:border-r-0 border-gray-200" : ""
                } ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""}`}
              >
                <service.icon className="w-6 h-6 text-[#002FA7] mb-5" />
                <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-4">
                  {service.title}
                </h3>
                <ul className="space-y-2">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#4B5563]">
                      <span className="w-1 h-1 rounded-full bg-[#002FA7] mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Governance, Security & Compliance */}
      <section data-testid="ai-governance-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
                AI Governance, Security & Compliance
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
                Responsible AI built into every solution
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed">
                Security, compliance, and ethical governance are not afterthoughts — they are
                foundational to every AI solution we deliver. We ensure your AI initiatives
                meet the highest standards of enterprise security and regulatory compliance.
              </p>
            </div>
            <div className="border border-gray-200">
              {governance.map((item, i) => (
                <div
                  key={item.text}
                  data-testid={`ai-governance-item-${i}`}
                  className={`flex items-start gap-4 p-6 ${
                    i < governance.length - 1 ? "border-b border-gray-200" : ""
                  }`}
                >
                  <item.icon className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#0A0A0A]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Delivery Lifecycle */}
      <section data-testid="ai-delivery-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              AI Delivery Lifecycle
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              From discovery to continuous optimization
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-gray-200 bg-white">
            {deliveryPhases.map((phase, i) => (
              <div
                key={phase.title}
                data-testid={`ai-phase-${i}`}
                className={`p-8 lg:p-10 ${
                  (i + 1) % 3 !== 0 ? "lg:border-r border-gray-200" : ""
                } ${(i + 1) % 2 !== 0 ? "sm:border-r lg:border-r-0 border-gray-200" : ""} ${
                  i < 3 ? "border-b border-gray-200" : ""
                } ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""}`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-heading text-2xl font-bold text-[#002FA7] opacity-30">
                    {phase.step}
                  </span>
                  <phase.icon className="w-5 h-5 text-[#002FA7]" />
                </div>
                <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">{phase.title}</h4>
                <p className="text-xs text-[#4B5563] leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section data-testid="ai-benefits-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
                Business Benefits
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
                Measurable impact from AI adoption
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed">
                Our AI services are designed to deliver tangible business outcomes — from
                operational cost reduction to enhanced decision making and customer experiences.
              </p>
            </div>
            <div className="border border-gray-200">
              {businessBenefits.map((benefit, i) => (
                <div
                  key={benefit}
                  className={`flex items-start gap-4 p-6 ${
                    i < businessBenefits.length - 1 ? "border-b border-gray-200" : ""
                  }`}
                >
                  <span className="font-heading text-sm font-bold text-[#002FA7]">0{i + 1}</span>
                  <p className="text-sm text-[#0A0A0A]">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why TEQXL for AI */}
      <section data-testid="ai-why-teqxl-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Why TEQXL for AI Services
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Enterprise AI, delivered right
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border border-gray-200 bg-white">
            {whyTeqxl.map((item, i) => (
              <div
                key={item.title}
                data-testid={`ai-why-item-${i}`}
                className={`p-6 lg:p-8 ${i < 4 ? "lg:border-r border-gray-200" : ""} ${
                  i < 3 ? "sm:border-b lg:border-b-0 border-gray-200" : ""
                } border-b lg:border-b-0 border-gray-200 last:border-b-0`}
              >
                <item.icon className="w-5 h-5 text-[#002FA7] mb-4" />
                <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">{item.title}</h4>
                <p className="text-xs text-[#4B5563] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white mb-6">
            Let's Build Intelligent Enterprises
          </h2>
          <p className="text-base text-blue-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            Whether you are exploring AI adoption, scaling existing AI initiatives, or embedding
            intelligence across enterprise workflows, TEQXL helps you deploy AI responsibly
            and at scale.
          </p>
          <Link
            to="/contact"
            data-testid="ai-cta-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#002FA7] text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Start Your AI Journey <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
